import { Link, useForm } from "@inertiajs/react"

import type { SharedProps } from "@/types"
import { AuthCard } from "@/components/auth-card"
import { AuthShell } from "@/components/auth-shell"
import { AuthStatus } from "@/components/auth-status"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

type Mode = "reset" | "setup"

const COPY = {
  reset: {
    heroTitle: "Your account is one step away.",
    heroDescription: "Create a strong new password to restore access to your account.",
    heroPoints: [
      "Use at least 12 characters for the best protection.",
      "Your link is single-use — complete this now.",
      "Once saved, you'll be taken straight to sign in.",
    ],
    eyebrow: "Recovery",
    cardTitle: "Reset your password",
    cardDescription: "Choose a new password to restore access to your account.",
    submitLabel: "Set new password",
    processingLabel: "Resetting...",
    backLabel: "Remembered it?",
    backLink: "Sign in instead",
  },
  setup: {
    heroTitle: "You're almost in.",
    heroDescription: "Create your password to activate your account — this is a one-time step.",
    heroPoints: [
      "This is a one-time setup — you won't need to do this again.",
      "Your link is single-use — complete this now.",
      "Once saved, sign in with your new password.",
    ],
    eyebrow: "Account setup",
    cardTitle: "Create your password",
    cardDescription: "Choose a password to access your account.",
    submitLabel: "Create password",
    processingLabel: "Creating password...",
    backLabel: "Already have an account?",
    backLink: "Sign in",
  },
} satisfies Record<Mode, {
  heroTitle: string; heroDescription: string; heroPoints: readonly string[]
  eyebrow: string; cardTitle: string; cardDescription: string
  submitLabel: string; processingLabel: string; backLabel: string; backLink: string
}>

type PasswordResetPageProps = SharedProps & {
  errorSummary: string[]
  mode: Mode
  paths: {
    resetPassword: string
    signIn: string
  }
  sid: string
}

export default function PasswordResetPage({
  errorSummary,
  flash,
  mode,
  paths,
  sid,
}: PasswordResetPageProps) {
  const copy = COPY[mode] ?? COPY["reset"]

  const form = useForm({
    password: "",
    password_confirmation: "",
    sid,
  })

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.patch(paths.resetPassword, {
      onFinish: () => form.reset("password", "password_confirmation"),
    })
  }

  return (
    <AuthShell
      heroDescription={copy.heroDescription}
      heroPoints={copy.heroPoints}
      heroTitle={copy.heroTitle}
      pageTitle={copy.cardTitle}
    >
      <div className="flex flex-col gap-4">
        <AuthStatus errorSummary={errorSummary} flash={flash} />
        <AuthCard
          description={copy.cardDescription}
          eyebrow={copy.eyebrow}
          title={copy.cardTitle}
        >
          <form onSubmit={submit}>
            <FieldGroup>
              <Field data-invalid={!!form.errors.password}>
                <FieldLabel htmlFor="password">New password</FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password}
                  autoComplete="new-password"
                  id="password"
                  name="password"
                  onChange={(event) => form.setData("password", event.target.value)}
                  required
                  type="password"
                  value={form.data.password}
                />
                <FieldDescription>Use at least 12 characters.</FieldDescription>
                <FieldError>{form.errors.password}</FieldError>
              </Field>
              <Field data-invalid={!!form.errors.password_confirmation}>
                <FieldLabel htmlFor="password_confirmation">
                  Confirm new password
                </FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password_confirmation}
                  autoComplete="new-password"
                  id="password_confirmation"
                  name="password_confirmation"
                  onChange={(event) =>
                    form.setData("password_confirmation", event.target.value)
                  }
                  required
                  type="password"
                  value={form.data.password_confirmation}
                />
                <FieldError>{form.errors.password_confirmation}</FieldError>
              </Field>
              <Field>
                <Button className="w-full" disabled={form.processing} type="submit">
                  {form.processing ? copy.processingLabel : copy.submitLabel}
                </Button>
                <FieldDescription className="text-center">
                  {copy.backLabel}{" "}
                  <Link href={paths.signIn}>{copy.backLink}</Link>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </AuthCard>
      </div>
    </AuthShell>
  )
}
